import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EarnProgressItem } from 'src/app/models/earned-reward';

@Component({
  selector: 'app-earn-progress-detail-dialog',
  template: `
    <div class="epd">
      <div class="epd-header">
        <h3 class="epd-title">{{data.offer_title || 'Earn a Reward'}}</h3>
        <span class="epd-close" (click)="dialogRef.close()">&times;</span>
      </div>
      <p class="epd-desc" *ngIf="data.offer_description && data.offer_description !== data.offer_title">
        {{data.offer_description}}
      </p>

      <div class="epd-reward-pill">
        {{data.reward_description}}
      </div>

      <div class="epd-card">
        <div class="epd-card-row">
          <span class="epd-card-label">How to earn</span>
          <span class="epd-card-value" *ngIf="data.trigger_type === 'SPEND'">
            Spend \${{data.spend_threshold?.toFixed(2)}}
          </span>
          <span class="epd-card-value" *ngIf="data.trigger_type !== 'SPEND'">
            Add {{data.trigger_type === 'ITEM' ? 'items' : 'categories'}}
          </span>
        </div>

        <div class="epd-card-row" *ngIf="data.trigger_type === 'SPEND'">
          <span class="epd-card-label">Progress</span>
          <span class="epd-card-value">
            \${{data.spend_current?.toFixed(2)}} / \${{data.spend_threshold?.toFixed(2)}}
          </span>
        </div>

        <div class="epd-bar" *ngIf="data.trigger_type === 'SPEND'">
          <div class="epd-bar-fill" [style.width.%]="getProgress()"></div>
        </div>

        <div *ngIf="data.trigger_type !== 'SPEND' && data.conditions?.length" class="epd-cond-list">
          <div class="epd-cond" *ngFor="let c of data.conditions" [class.met]="c.met">
            <span class="epd-cond-check" *ngIf="c.met">&#10003;</span>
            <span class="epd-cond-check unmet" *ngIf="!c.met">&#9675;</span>
            <span class="epd-cond-name">{{c.name}}</span>
            <span class="epd-cond-qty">{{c.quantity_in_cart}}/{{c.quantity_required}}</span>
          </div>
        </div>
      </div>

      <div class="epd-status" [class.qualified]="data.qualified">
        {{data.qualified ? 'Qualified! Complete your order to earn this.' : 'Keep adding to qualify.'}}
      </div>

      <div class="epd-actions">
        <button class="epd-btn" (click)="dialogRef.close()">Got it</button>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }

    .epd { padding: 20px; }

    .epd-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
    .epd-title { font-size: 17px; font-weight: 700; color: var(--text, #171717); margin: 0; }
    .epd-close { font-size: 24px; color: var(--text-muted, #616161); cursor: pointer; line-height: 1; padding: 4px 4px; border-radius: 50%; }
    .epd-close:hover { background: var(--field-bg, #f5f5f5); color: var(--text, #171717); }

    .epd-desc { font-size: 13px; color: var(--text-muted, #616161); margin: 0 0 12px; line-height: 1.4; }

    .epd-reward-pill {
      display: inline-block;
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 700;
      background: var(--brand-tint, #e8f0fe);
      color: var(--brand-accent, #1967d2);
      margin-bottom: 16px;
    }

    .epd-card {
      background: var(--field-bg, #F5F5F5);
      border-radius: 12px;
      padding: 14px 16px;
      margin-bottom: 16px;
    }

    .epd-card-row { display: flex; justify-content: space-between; align-items: center; padding: 5px 0; }
    .epd-card-label { font-size: 12px; color: var(--text-muted, #888); }
    .epd-card-value { font-size: 13px; color: var(--text, #333); font-weight: 600; }

    .epd-bar { height: 8px; background: var(--divider, #E0E0E0); border-radius: 4px; overflow: hidden; margin: 8px 0 4px; }
    .epd-bar-fill { height: 100%; background: var(--brand, #0c83ff); border-radius: 4px; transition: width 0.3s ease; min-width: 4px; }

    .epd-cond-list { margin-top: 8px; }
    .epd-cond { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-top: 1px solid var(--divider, #E0E0E0); }
    .epd-cond:first-child { border-top: none; }
    .epd-cond-check { font-size: 14px; color: var(--brand, #0c83ff); flex-shrink: 0; }
    .epd-cond-check.unmet { color: var(--text-muted, #999); }
    .epd-cond-name { flex: 1; font-size: 13px; color: var(--text, #333); }
    .epd-cond-qty { font-size: 12px; color: var(--text-muted, #888); }

    .epd-status {
      padding: 12px 14px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      text-align: center;
      background: #fef9c3;
      color: #854d0e;
    }
    .epd-status.qualified {
      background: var(--brand-tint, #dcfce7);
      color: var(--brand-accent, #166534);
    }

    .epd-actions { display: flex; justify-content: flex-end; margin-top: 16px; }
    .epd-btn {
      padding: 8px 24px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      background: var(--brand, #0c83ff);
      color: var(--on-brand, #fff);
    }
    .epd-btn:hover { opacity: 0.9; }

    :host-context(.storefront-corner-sharp) .epd-reward-pill { border-radius: 4px; }
    :host-context(.storefront-corner-sharp) .epd-card { border-radius: 4px; }
    :host-context(.storefront-corner-sharp) .epd-status { border-radius: 4px; }
    :host-context(.storefront-corner-sharp) .epd-btn { border-radius: 4px; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EarnProgressDetailDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<EarnProgressDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EarnProgressItem
  ) {}

  getProgress(): number {
    if (this.data.trigger_type !== 'SPEND' || !this.data.spend_threshold) return 0;
    return Math.min(100, (this.data.spend_current / this.data.spend_threshold) * 100);
  }
}
